import { useState} from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Bell, Mail, BookOpen, Code, Brain, Target, Users, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {Alert} from "@/components/Alert.tsx";


export function ComingSoonPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const features = [
    {
      icon: BookOpen,
      title: "Interactive Learning",
      description: "Engaging content with hands-on practice"
    },
    {
      icon: Code,
      title: "Live Code Editor",
      description: "Write and test code in real-time"
    },
    {
      icon: Brain,
      title: "AI-Powered Learning",
      description: "Personalized learning paths"
    },
    {
      icon: Target,
      title: "Interview Prep",
      description: "Company-specific practice"
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://epvnbirwcqixskyllbmh.supabase.co/rest/v1/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVwdm5iaXJ3Y3FpeHNreWxsYm1oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzIyMjAwNjcsImV4cCI6MjA0Nzc5NjA2N30.muSl0Qps-4fZQbJtbgMCosEnoEJ-LRwx8EXNGe57Ask',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVwdm5iaXJ3Y3FpeHNreWxsYm1oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzIyMjAwNjcsImV4cCI6MjA0Nzc5NjA2N30.muSl0Qps-4fZQbJtbgMCosEnoEJ-LRwx8EXNGe57Ask',
        },
        body: JSON.stringify({
          email,
          subscribed_at: new Date().toISOString(),
          status: 'ADDED'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      setStatus({
        type: 'success',
        message: 'Thanks for joining! Check your email for confirmation.'
      });
      setEmail('');
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
    console.log('Subscribed:', email);
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
              Master DSA Like Never Before
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A revolutionary platform combining interactive learning, real-time practice, 
              and AI-powered guidance to help you ace technical interviews.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  placeholder="Enter your email for early access"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 pr-4 py-3 w-full sm:w-96 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-blue-700 transition-colors"
              >
                <span>Get Early Access</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
            {status.message && (
                status.type === 'success' ? (
                    <Alert
                        type="success"
                        title="Success!"
                        description={status.message} // Use curly braces here
                        autoDismiss={true}
                        dismissTime={5000}
                        onDismiss={() => console.log('Alert dismissed')}
                    />
                ) : (
                    <Alert
                        type="error"
                        title="We are broke!"
                        description="We messed up" // Corrected the wording slightly
                        autoDismiss={true}
                        dismissTime={5000}
                        onDismiss={() => console.log('Alert dismissed')}
                    />
                )
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-sm text-gray-500"
          >
            🚀 Launch Date: Coming Soon • 🎯 Limited Early Access Available
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center"
        >
          <h2 className="text-3xl font-bold mb-6">Why Wait?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="flex flex-col items-center">
              <Users className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Join the Community</h3>
              <p className="text-gray-600">Learn with like-minded developers</p>
            </div>
            <div className="flex flex-col items-center">
              <Brain className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Accelerate Learning</h3>
              <p className="text-gray-600">Master DSA concepts faster</p>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Get Job Ready</h3>
              <p className="text-gray-600">Prepare for top tech companies</p>
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Join the Waitlist
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Learn More
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}